// Rate limiting for crypto API calls
let lastPriceRequestTime = 0;
const MIN_PRICE_REQUEST_INTERVAL = 60000; // 60 seconds minimum between price requests

// Cache for API responses to avoid rate limiting
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 300000; // 5 minutes cache for price data

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const getCachedData = (key: string) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedData = (key: string, data: any) => {
  cache.set(key, { data, timestamp: Date.now() });
};

const rateLimitPrice = async () => {
  const now = Date.now();
  const timeSinceLastRequest = now - lastPriceRequestTime;
  
  if (timeSinceLastRequest < MIN_PRICE_REQUEST_INTERVAL) {
    await delay(MIN_PRICE_REQUEST_INTERVAL - timeSinceLastRequest);
  }
  
  lastPriceRequestTime = Date.now();
};

export interface CryptoPrice {
  symbol: string;
  name: string;
  current_price: number;
  price_change_24h: number;
  price_change_percentage_24h: number;
  market_cap: number;
  volume_24h: number;
  last_updated: string;
}

export interface MarketStats {
  total_market_cap: number;
  total_volume: number;
  market_cap_change_percentage_24h: number;
  active_cryptocurrencies: number;
}

export interface XMRStats {
  price: CryptoPrice;
  marketStats: MarketStats;
  businessCount: number;
  countriesCount: number;
  lastUpdated: string;
}

// Fetch XMR price from CoinGecko with proper error handling and caching
export const fetchXMRPrice = async (): Promise<CryptoPrice> => {
  const cacheKey = 'xmr_price_live';
  const cached = getCachedData(cacheKey);
  if (cached) {
    console.log('Using cached XMR price data');
    return cached;
  }

  await rateLimitPrice();

  try {
    console.log('Fetching live XMR price from CoinGecko...');
    
    // Use the simple price endpoint first (more reliable)
    const response = await fetch(
      'https://api.coingecko.com/api/v3/simple/price?ids=monero&vs_currencies=usd&include_24hr_change=true&include_market_cap=true&include_24hr_vol=true&include_last_updated_at=true',
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      }
    );

    if (!response.ok) {
      throw new Error(`CoinGecko API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('CoinGecko XMR response:', data);

    if (!data.monero) {
      throw new Error('No Monero data in CoinGecko response');
    }

    const moneroData = data.monero;

    // Validate the data
    if (typeof moneroData.usd !== 'number' || moneroData.usd <= 0) {
      throw new Error('Invalid price data from CoinGecko');
    }

    const priceData: CryptoPrice = {
      symbol: 'XMR',
      name: 'Monero',
      current_price: moneroData.usd,
      price_change_24h: moneroData.usd_24h_change || 0,
      price_change_percentage_24h: moneroData.usd_24h_change || 0,
      market_cap: moneroData.usd_market_cap || 0,
      volume_24h: moneroData.usd_24h_vol || 0,
      last_updated: moneroData.last_updated_at ? new Date(moneroData.last_updated_at * 1000).toISOString() : new Date().toISOString(),
    };

    console.log('Processed XMR price data:', priceData);

    // Validate processed data
    if (priceData.current_price <= 0) {
      throw new Error('Invalid processed price data');
    }

    setCachedData(cacheKey, priceData);
    return priceData;
  } catch (error) {
    console.error('Error fetching XMR price from CoinGecko:', error);
    
    // Fallback to Binance API
    try {
      console.log('Trying Binance API as fallback...');
      const binanceResponse = await fetch('https://api.binance.com/api/v3/ticker/24hr?symbol=XMRUSDT', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });

      if (binanceResponse.ok) {
        const binanceData = await binanceResponse.json();
        console.log('Binance XMR response:', binanceData);
        
        const price = parseFloat(binanceData.lastPrice);
        const priceChange = parseFloat(binanceData.priceChange);
        const priceChangePercent = parseFloat(binanceData.priceChangePercent);
        const volume = parseFloat(binanceData.volume);

        // Validate Binance data
        if (isNaN(price) || price <= 0) {
          throw new Error('Invalid Binance price data');
        }

        const binancePrice: CryptoPrice = {
          symbol: 'XMR',
          name: 'Monero',
          current_price: price,
          price_change_24h: isNaN(priceChange) ? 0 : priceChange,
          price_change_percentage_24h: isNaN(priceChangePercent) ? 0 : priceChangePercent,
          market_cap: 0, // Binance doesn't provide market cap
          volume_24h: isNaN(volume) ? 0 : volume * price,
          last_updated: new Date().toISOString(),
        };
        
        console.log('Binance XMR price data:', binancePrice);
        setCachedData(cacheKey, binancePrice);
        return binancePrice;
      }
    } catch (binanceError) {
      console.error('Binance API also failed:', binanceError);
    }
    
    // If all APIs fail, throw error
    throw new Error('Unable to fetch live XMR price data. All cryptocurrency APIs are currently unavailable.');
  }
};

// Fetch global crypto market stats
export const fetchMarketStats = async (): Promise<MarketStats> => {
  const cacheKey = 'market_stats_live';
  const cached = getCachedData(cacheKey);
  if (cached) {
    console.log('Using cached market stats');
    return cached;
  }

  try {
    console.log('Fetching live market stats from CoinGecko...');
    const response = await fetch('https://api.coingecko.com/api/v3/global', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      }
    });
    
    if (!response.ok) {
      throw new Error(`CoinGecko global API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('CoinGecko global market response:', data);

    if (!data.data) {
      throw new Error('No global market data available');
    }

    const globalData = data.data;

    const marketStats: MarketStats = {
      total_market_cap: globalData.total_market_cap?.usd || 0,
      total_volume: globalData.total_volume?.usd || 0,
      market_cap_change_percentage_24h: globalData.market_cap_change_percentage_24h_usd || 0,
      active_cryptocurrencies: globalData.active_cryptocurrencies || 0,
    };

    console.log('Processed market stats:', marketStats);
    setCachedData(cacheKey, marketStats);
    return marketStats;
  } catch (error) {
    console.error('Error fetching market stats:', error);
    
    // Return empty stats instead of fake data
    const emptyStats: MarketStats = {
      total_market_cap: 0,
      total_volume: 0,
      market_cap_change_percentage_24h: 0,
      active_cryptocurrencies: 0,
    };
    
    return emptyStats;
  }
};

// Get comprehensive XMR statistics with proper error handling
export const fetchXMRStats = async (businessCount: number = 0): Promise<XMRStats> => {
  try {
    console.log('Fetching comprehensive XMR stats...');
    
    // Fetch price data (required)
    const price = await fetchXMRPrice();
    
    // Fetch market stats (optional, can fail)
    let marketStats: MarketStats;
    try {
      marketStats = await fetchMarketStats();
    } catch (error) {
      console.warn('Market stats failed, using empty data:', error);
      marketStats = {
        total_market_cap: 0,
        total_volume: 0,
        market_cap_change_percentage_24h: 0,
        active_cryptocurrencies: 0,
      };
    }

    const stats: XMRStats = {
      price,
      marketStats,
      businessCount,
      countriesCount: 15, // Based on our verified business locations
      lastUpdated: new Date().toISOString(),
    };

    console.log('Final XMR stats:', stats);
    return stats;
  } catch (error) {
    console.error('Error fetching XMR stats:', error);
    throw error;
  }
};

// Format currency values with proper error handling
export const formatCurrency = (value: number, currency: string = 'USD'): string => {
  if (typeof value !== 'number' || isNaN(value) || value === 0) {
    return 'Loading...';
  }
  
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    return `$${value.toFixed(2)}`;
  }
};

// Format large numbers (market cap, volume) with proper error handling
export const formatLargeNumber = (value: number): string => {
  if (typeof value !== 'number' || isNaN(value) || value === 0) {
    return 'N/A';
  }
  
  try {
    if (value >= 1e12) {
      return `$${(value / 1e12).toFixed(2)}T`;
    } else if (value >= 1e9) {
      return `$${(value / 1e9).toFixed(2)}B`;
    } else if (value >= 1e6) {
      return `$${(value / 1e6).toFixed(2)}M`;
    } else if (value >= 1e3) {
      return `$${(value / 1e3).toFixed(2)}K`;
    }
    return `$${value.toFixed(2)}`;
  } catch (error) {
    return `$${value.toFixed(2)}`;
  }
};

// Format percentage change with proper error handling
export const formatPercentage = (value: number): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '0.00%';
  }
  
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

// Get price trend indicator
export const getPriceTrend = (change: number): 'up' | 'down' | 'neutral' => {
  if (typeof change !== 'number' || isNaN(change)) {
    return 'neutral';
  }
  
  if (change > 0.1) return 'up';
  if (change < -0.1) return 'down';
  return 'neutral';
};

// Test API connectivity
export const testAPIConnectivity = async (): Promise<boolean> => {
  try {
    const response = await fetch('https://api.coingecko.com/api/v3/ping', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      }
    });
    return response.ok;
  } catch (error) {
    console.error('API connectivity test failed:', error);
    return false;
  }
};